import { getStructuralPath } from '@fragment/path-utils/path';
import {
  type BaseChartOfAccountsType,
  SchemaEntryGroup,
  type SchemaEntryGroupType,
} from '@fragment/types/schemas';
import { safeGetZodErrors } from '@fragment/zod-utils/src';

import type { transformAndValidateEntries } from './entries';
import {
  getStructuralPathToAccount,
  validateAccountIsActive,
  validateAccountPath,
  validateTemplateAccountParameters,
} from './utilities';

type ValidatedEntryArray = ReturnType<
  typeof transformAndValidateEntries
>['filledIn']['types'];

export type GroupValidationResult = {
  message: string;
  path: (string | number)[];
};

/**
 * Helper function to check if two account paths are semantically equivalent
 * by comparing their structural paths (ignoring template parameters)
 */
const isAccountPathEquivalent = (path1: string, path2: string): boolean => {
  return getStructuralPath(path1) === getStructuralPath(path2);
};

/**
 * Validate that the group has at least 2 steps
 * @param group
 * @returns
 */
const validateMinimumSteps = (
  group: SchemaEntryGroupType,
  entries: ValidatedEntryArray
) => {
  if (entries.length < 2 && group.reconciliation) {
    return [
      {
        message: 'Reconciliation Group must contain at least 2 entries',
        path: ['entries'],
      },
    ];
  }
  if (entries.length < 1 && !group.reconciliation) {
    return [
      {
        message: 'Group must contain at least 1 entry',
        path: ['entries'],
      },
    ];
  }
  return [];
};

/**
 * Validate that the group has unique entries
 * @param group
 * @returns
 */
const validateUniqueEntries = (entries: ValidatedEntryArray) => {
  const errors: GroupValidationResult[] = [];
  entries.forEach((entry, index) => {
    if (entry.groups) {
      const groupSet = new Set(entry.groups.map((g) => `${g.key}-${g.value}`));
      if (groupSet.size !== entry.groups.length) {
        errors.push({
          message: 'Entry cannot have duplicate groups',
          path: ['entries', index, 'groups'],
        });
      }
    }
  });
  return errors;
};

/** validate that reconciliation group has a clearing account path */
const validateReconciliationGroupHasClearingAccountPath = (
  group: SchemaEntryGroupType
) => {
  if (
    group.reconciliation &&
    group.reconciliation.clearingAccountPath.path.trim() === ''
  ) {
    return [
      {
        message: 'Reconciliation group must have a clearing account path',
        path: ['reconciliation', 'clearingAccountPath'],
      },
    ];
  }
  return [];
};

/**
 * Validate that the group entries modify the clearing account if a recon group
 * @param group
 * @param validatedEntries
 * @returns
 */
const validateEntriesModifyClearingAccount = (
  group: SchemaEntryGroupType,
  entriesInGroup: ValidatedEntryArray
) => {
  // skip if not a reconciliation group
  if (!group.reconciliation) {
    return [];
  }
  const clearingAccount = group.reconciliation.clearingAccountPath.path;

  const entriesMissingClearingAccount = entriesInGroup.filter(
    (e) =>
      !e?.lines?.some((l) =>
        isAccountPathEquivalent(l.account.path, clearingAccount)
      )
  );

  if (entriesMissingClearingAccount.length > 0) {
    return entriesMissingClearingAccount.map((e) => ({
      message: `All entries in a reconciliation group must modify the clearing account, not present in the entries ${e?.type} v${e?.typeVersion}`,
      path: ['entries'],
    }));
  }

  return [];
};

/**
 * Validate that the group clearing account is valid
 * @param group
 * @param validatedCoA
 * @returns
 */
export const validateClearingAccount = (
  group: SchemaEntryGroupType,
  validatedCoA: BaseChartOfAccountsType
) => {
  // Only validate clearing account if it's a reconciliation group or if clearing account path is provided
  if (!group.reconciliation) {
    return [];
  }

  const clearingAccount = group.reconciliation.clearingAccountPath.path;

  // Check if clearing account path is empty
  if (!clearingAccount || clearingAccount.trim() === '') {
    return [
      {
        message: 'Group clearing account path cannot be empty',
        path: ['reconciliation', 'clearingAccountPath'],
      },
    ];
  }
  const accountPathToAccount = getStructuralPathToAccount(validatedCoA);
  const errors: GroupValidationResult[] = [];

  // Validate the clearing account path
  errors.push(
    ...validateAccountPath(
      clearingAccount,
      accountPathToAccount,
      'Group clearing account',
      ['reconciliation', 'clearingAccountPath']
    )
  );

  // If account exists, validate it's active
  errors.push(
    ...validateAccountIsActive(
      clearingAccount,
      accountPathToAccount,
      'Group clearing account',
      ['reconciliation', 'clearingAccountPath']
    )
  );

  // Validate template account parameters if applicable
  errors.push(
    ...validateTemplateAccountParameters(
      clearingAccount,
      accountPathToAccount,
      'Group clearing account',
      ['reconciliation', 'clearingAccountPath']
    )
  );

  return errors;
};

const setErrorPathPrefix = (
  errors: GroupValidationResult[],
  index: number
): GroupValidationResult[] => {
  return errors.map((e) => ({
    ...e,
    path: ['groups', index, ...e.path],
  }));
};

/**
 * Main validation function that applies all group-specific validation rules
 */
export const validateGroup = (
  group: SchemaEntryGroupType,
  validatedCoA: BaseChartOfAccountsType,
  validatedEntries: ReturnType<typeof transformAndValidateEntries>['filledIn'],
  index: number
): GroupValidationResult[] => {
  const errors: GroupValidationResult[] = [];

  // Validate the base schema using Zod
  const zodErrors = safeGetZodErrors(SchemaEntryGroup, group, []);
  errors.push(...setErrorPathPrefix(zodErrors, index));

  // If there are Zod parsing errors, return them early
  if (errors.length > 0) {
    return errors;
  }

  // get entries containing this group
  const entriesInGroup = validatedEntries.types.filter((e) =>
    e.groups.some((g) => g.key === group.key)
  );

  // Apply all group-specific validation rules
  errors.push(
    ...setErrorPathPrefix(
      validateReconciliationGroupHasClearingAccountPath(group),
      index
    )
  );
  errors.push(
    ...setErrorPathPrefix(validateMinimumSteps(group, entriesInGroup), index)
  );
  errors.push(...validateUniqueEntries(validatedEntries.types));

  if (errors.length === 0) {
    errors.push(
      ...setErrorPathPrefix(validateClearingAccount(group, validatedCoA), index)
    );
    errors.push(
      ...setErrorPathPrefix(
        validateEntriesModifyClearingAccount(group, entriesInGroup),
        index
      )
    );
  }
  return errors;
};
